import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useDataProvider, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import { useCosmos } from 'providers/contexts/CosmosContext';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import type { AssetQuickLaunchPrefill } from 'components/editorial/AssetQuickLaunch/types';
import {
  ApiErrorResponse,
  BloxCustomDataProviderType,
  PatchType,
  PostEditorialAssetDuplicateFullParams,
  PostEditorialAssetDuplicateResult,
} from 'utility/types/dataProvider';
import { applyQuickLaunchPrefillPatch } from './duplicateSharedUtils';

type Options = {
  invalidateQueries?: boolean | string[];
  onSuccess?: (newUuid: string) => void;
  onError?: (error: ApiErrorResponse) => void;
};

type DuplicateArgs = {
  id: string;
  title: string;
  quickLaunchPrefill?: AssetQuickLaunchPrefill;
  additionalPatches?: PatchType[];
  isTemplateDuplication?: boolean;
};

type DuplicateMutationVariables = PostEditorialAssetDuplicateFullParams & {
  quickLaunchPrefill?: AssetQuickLaunchPrefill;
};

export function useDuplicateAsset(options: Options = {}) {
  const dataProvider = useDataProvider<BloxCustomDataProviderType>();
  const notify = useNotify();
  const { queryClient } = useCosmosQueryClient();
  const { activeSite } = useCosmos();
  const { t } = useTranslation();

  const { mutateAsync, status } = useMutation<
    PostEditorialAssetDuplicateResult,
    ApiErrorResponse,
    DuplicateMutationVariables
  >({
    mutationKey: ['duplicateAssetById'],
    mutationFn: ({ quickLaunchPrefill: _prefill, ...rest }) =>
      dataProvider.duplicateAssetById(rest),
    onSuccess: async (resp, variables) => {
      const newUuid = resp.data?.uuid;
      if (!newUuid) {
        console.warn('duplicate succeeded, but no uuid returned');
        return;
      }

      const quickLaunchPrefill = variables?.quickLaunchPrefill;
      try {
        await applyQuickLaunchPrefillPatch(dataProvider, newUuid, quickLaunchPrefill);
      } catch (error) {
        console.error('[useDuplicateAsset] Failed to apply quick launch values:', error);
        notify(t('blox.editorial.assetQuickLaunch.apply.warning_duplicate'), {
          type: 'warning',
        });
      }

      // Default invalidation - refresh React Admin's list
      if (options.invalidateQueries) {
        const keyArray =
          options.invalidateQueries === true
            ? ['editorial', 'getInfiniteList']
            : options.invalidateQueries;

        // Check if it's an editorial resource that needs refreshing
        if (Array.isArray(keyArray) && keyArray.length >= 1) {
          queryClient.invalidateQueries({ queryKey: keyArray });
        }
      }

      // Open the editor page for the new asset
      window.open(`/${activeSite}/editorial/edit/${newUuid}`, '_blank');
      options.onSuccess?.(newUuid);
    },
    onError: (err) => {
      notify(`${t('blox.general.error.label')}: ${err.message || err.axiosMessage}`, {
        type: 'error',
      });
      options.onError?.(err);
    },
  });

  const duplicate = useCallback(
    async ({
      id,
      title,
      quickLaunchPrefill,
      additionalPatches = [],
      isTemplateDuplication = false,
    }: DuplicateArgs) => {
      const basePatch: PatchType[] = [
        {
          path: '/title',
          op: 'add',
          value: isTemplateDuplication ? title : `${title} (Copy)`,
        },
        { path: '/published', op: 'add', value: 0 },
        { path: '/draft', op: 'add', value: true },
        ...additionalPatches,
      ];

      const payload: DuplicateMutationVariables = {
        path: { id },
        body: {
          patch: basePatch,
        },
        quickLaunchPrefill,
      };
      return mutateAsync(payload);
    },
    [mutateAsync],
  );

  return { duplicate, status };
}
